import request from "@/utils/request.js";
import SparkMD5 from 'spark-md5'

const CHUNK_SIZE = 1 * 1024 * 1024; // 2MB 分片大小

/*
 * 分片上传，实现类
 */
export default class ChunkUpload {
  uploading = false
  fileObject = null
  uploadId = ''

  totalChunks = 0 // 总分片数
  uploadedChunks = 0// 已上传分片数

  cancelUploadFlag = false // 是否主动取消上传
  currentAbortController = null // 当前活跃的请求控制器（用于取消请求）

  onUploadProgress = (progress) => {} // 进度回调

  // 内部：取消上传
  cancelUpApi() {
    this.cancelUploadFlag = true;
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
  }

  //内部：上传单个分片
  async uploadChunk(chunkIndex, chunkData) {
    const formData = new FormData();
    formData.append('file', chunkData);
    formData.append('chunkIndex', chunkIndex);      // 当前分片索引（从0开始）
    formData.append('uploadId', this.uploadId);      // 文件唯一标识

    // 创建 AbortController 用于取消请求
    const abortController = new AbortController();
    this.currentAbortController = abortController;

    try {
      const response = await request({
        url: '/file/upload/chunk',
        method: 'post',
        data: formData,
        timeout: 60 * 1000, // 每个分片60秒超时
        signal: abortController.signal, // 传递取消信号
        headers: {
          'Content-Type': 'multipart/form-data',
          repeatSubmit: false
        }
      });

      // 检查业务响应（根据实际后端返回结构调整）
      if (response.code && response.code !== 200) {
        throw new Error(response.message || '上传分片失败');
      }

      return response;
    } catch (error) {
      // 如果是主动取消，不抛出错误
      if (error.name === 'AbortError' || this.cancelUploadFlag) {
        throw new Error('upload_cancelled');
      }
      throw error;
    } finally {
      if (this.currentAbortController === abortController) {
        this.currentAbortController = null;
      }
    }
  }

  // 内部接口1：初始化，获取uploadId，便于后端记录
  async apiInit() {
    const res = await request({
      url: '/file/upload/init',
      method: 'post',
      data: {
        fileMd5: this.fileObject.md5,
        fileName: this.fileObject.name,
        fileSize: this.fileObject.size,
        totalChunks: this.totalChunks,
      },
      headers: {
        repeatSubmit: false
      },
      timeout: 60 * 1000,
    });
    this.uploadId = res.data.uploadId
  }

  // 内部接口2：检查已经传了的(断点续传)
  async apiCheck() {
    const res = await request({
      url: '/file/upload/check',
      method: 'post',
      data: {
        uploadId: this.uploadId
      },
      timeout: 60 * 1000,
      headers: {
        repeatSubmit: false
      },
    });
    return res.data
  }

  // 内部接口3：上传完成merge
  async apiMerge() {
    const res = await request({
      url: '/file/upload/merge',
      method: 'post',
      data: {
        uploadId: this.uploadId
      },
      timeout: 60 * 1000,
      headers: {
        repeatSubmit: false
      },
    });
    return res.data
  }

  // 内部接口4：取消上传
  apiStop() {
    if (!this.uploadId) {
      return
    }

    const upId = this.uploadId
    this.uploadId = '';

    setTimeout(async () => {
      await request({
        url: '/file/upload/cancel',
        method: 'post',
        data: {
          uploadId: upId
        },
        headers: {
          repeatSubmit: false
        },
        timeout: 60 * 1000,
      });
    }, 3000)
  }

  // 内部：执行分片上传
  async performChunkUpload() {
    const file = this.fileObject;
    if (!file) {
      throw new Error('文件不存在');
    }

    let toUpChunks = Array.from({length: this.totalChunks}, (_, i) => i)

    const alreadyUpload = await this.apiCheck() // 已经上传的分片，本次上传跳过
    this.uploadedChunks = alreadyUpload.length
    toUpChunks = toUpChunks.filter(f => !alreadyUpload.some(s => s == f)).reverse()
    while (toUpChunks.length) {
      // 检查是否取消
      if (this.cancelUploadFlag) {
        throw new Error('上传已取消');
      }
      const parallelCount = 2 // 并行上传数
      const parallelUploader = []
      for (let i = 0; i < parallelCount; i++) {
        const curChunk = toUpChunks.pop()
        if (curChunk != null) {
          // 计算分片起始位置
          const start = curChunk * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunkData = file.slice(start, end);

          parallelUploader.push({curChunk, chunkData})
        }
      }
      await Promise.all(parallelUploader.map(m => this.uploadChunk(m.curChunk, m.chunkData)))

      this.uploadedChunks += parallelUploader.length   // 更新进度


      let progress
      if (this.uploadedChunks == 0 || this.totalChunks == 0) {
        progress = 0
      } else {
        progress = Math.floor((this.uploadedChunks / this.totalChunks) * 100);
      }
      this.onUploadProgress(progress)
      console.log('上传进度', progress + '%')
    }
  }

  // 内部：分片数量计算
  async calcChoiceFile(file) {
    try {
      this.resetUploadState()
      const md5 = await calculateFileMD5(file);
      console.log(`【${file.name}】完整MD5：`, md5);

      file.md5 = md5
      this.fileObject = file;
      this.totalChunks = Math.ceil(file.size / CHUNK_SIZE); // 计算分片树
      this.uploadedChunks = 0;
      // 重置取消标志
      this.cancelUploadFlag = false;
    } catch (e) {
      console.error('计算md5失败', e)
      throw e
    }
  }

  // 内部：确认上传
  async startUpload() {
    // 校验文件是否已选择
    if (!this.fileObject) {
      return;
    }

    // 防止重复上传
    if (this.uploading) {
      return;
    }

    this.uploading = true;
    this.cancelUploadFlag = false;

    try {
      await this.apiInit() // 初始化，获取uploadId

      // 执行分片上传
      await this.performChunkUpload();

      const data = await this.apiMerge()
      data.name = this.fileObject.name
      return {data}
      // 所有分片上传成功
    } catch (error) {
      // 处理错误
      if (error.message === 'upload_cancelled' || error.message == 'canceled' || error?.toString().indexOf('已取消') > -1) {
        console.log('上传已取消', error)
        this.apiStop()
        throw error
      } else {
        console.error('分片上传失败:', error);
        this.apiStop()
        throw error
      }
    } finally {
      this.uploading = false;
      this.currentAbortController = null;
    }
  }

  // 内部：重置上传相关状态
  resetUploadState() {
    if (this.uploading) {
      this.cancelUpApi();
    }
    this.fileObject = null;
    this.totalChunks = 0;
    this.uploadedChunks = 0;
    this.uploading = false;
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }

    // 不重置，防止取消过快，无副作用
    this.cancelUploadFlag = true;
  }

  /*
   * 外部：启动函数
   * @param file文件
   */
  async exeStartUpload(file) {
    await this.calcChoiceFile(file)
    return await this.startUpload()
  }

  /*
   * 外部：停止上传
   */
  cancel(reason) {
    this.apiStop()
    this.cancelUpApi()
  }

  constructor(onProgress){
    if (onProgress != null && typeof onProgress == 'function') {
      this.onUploadProgress = onProgress
    }
  }
}

/**
 * 分片计算文件MD5核心函数
 */
const calculateFileMD5 = async (file) => {
  let progress = 0; // 当前md5计算进度
  const md5CalcChunk = 10 * 1024 * 1024; // 2MB 分片大小
  return new Promise((resolve, reject) => {
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();
    // 总分片数
    const totalChunks = Math.ceil(file.size / md5CalcChunk);
    // 当前处理的分片索引
    let currentChunk = 0;

    // 读取下一个分片
    const loadNextChunk = () => {
      // 计算当前分片的起始/结束位置
      const start = currentChunk * md5CalcChunk;
      const end = Math.min(start + md5CalcChunk, file.size);
      // 切割文件分片
      const chunk = file.slice(start, end);

      // 读取分片为ArrayBuffer
      fileReader.readAsArrayBuffer(chunk);
      // 更新进度（可选）
      progress = Math.floor((currentChunk / totalChunks) * 100);
      console.log(`MD5计算进度：${progress}%`);
    };

    // 分片读取完成回调
    fileReader.onload = (e) => {
      // 将分片内容加入MD5计算
      spark.append(e.target.result);
      currentChunk++;

      // 所有分片处理完成
      if (currentChunk >= totalChunks) {
        progress = 100;
        const md5 = spark.end();
        resolve(md5);
        return;
      }

      // 继续处理下一个分片
      loadNextChunk();
    };

    // 读取失败回调
    fileReader.onerror = (error) => {
      reject(new Error(`文件分片读取失败：${error.message}`));
    };

    // 开始处理第一个分片
    loadNextChunk();
  });
};
